import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChefHat, Users, MapPin, Clock, Heart, Truck, ShoppingBag, ArrowRight, Sparkles, Zap } from 'lucide-react';
import '../LandingPage/LandingPage.css';

const LandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('buyers');

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section8">
        <div className="hero-container8">
          <div className="hero-content8">
            <div className="hero-badge8">
              <Sparkles size={16} />
              <span>Connecting Communities Through Food</span>
            </div>
            
            <h1 className="hero-title8">
              Where Every Meal
              <br />
              <span className="title-highlight">Tells a Story</span>
            </h1>
            
            <p className="hero-description">
              Join thousands discovering authentic homemade meals from passionate cooks in their neighborhood. 
              From grandma's secret recipes to innovative fusion dishes - every bite connects you to real people and real stories.
            </p>
            
            <div className="hero-actions">
              <Link to="/signup" className="primary-cta">
                <span>Start Your Journey</span>
                <ArrowRight size={20} />
              </Link>
            </div>
            
            <div className="hero-features">
              <div className="feature-pill">
                <Zap size={16} />
                <span>Instant Connection</span>
              </div>
              <div className="feature-pill">
                <Heart size={16} />
                <span>Made with Love</span>
              </div>
              <div className="feature-pill">
                <MapPin size={16} />
                <span>Hyperlocal</span>
              </div>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="visual-grid">
              <div className="food-card card-1">
                <img src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg" alt="Delicious bowl" />
                <div className="card-overlay">
                  <span className="dish-category">Healthy Bowls</span>
                  <span className="dish-name">Mediterranean Bowl</span>
                </div>
              </div>
              
              <div className="food-card card-2">
                <img src="https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg" alt="Fresh pasta" />
                <div className="card-overlay">
                  <span className="dish-category">Fresh Pasta</span>
                  <span className="dish-name">Homemade Linguine</span>
                </div>
              </div>
              
              <div className="food-card card-3">
                <img src="https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg" alt="Artisan bread" />
                <div className="card-overlay">
                  <span className="dish-category">Artisan Bread</span>
                  <span className="dish-name">Sourdough Special</span>
                </div>
              </div>
            </div>
            
            <div className="floating-elements">
              <div className="floating-icon icon-1">
                <ChefHat size={24} />
              </div>
              <div className="floating-icon icon-2">
                <Heart size={20} />
              </div>
              <div className="floating-icon icon-3">
                <Truck size={22} />
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose HomeCookt?</h2>
            <p className="section-subtitle">
              Experience the difference of authentic, homemade food made with love
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon authentic">
                <Heart size={32} />
              </div>
              <h3>Authentic Flavors</h3>
              <p>Real homemade food prepared with traditional recipes and fresh ingredients by passionate home cooks.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon local">
                <MapPin size={32} />
              </div>
              <h3>Local Community</h3>
              <p>Support your neighbors and discover amazing cooks right in your area. Build connections through food.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon fast">
                <Clock size={32} />
              </div>
              <h3>Quick & Fresh</h3>
              <p>Get freshly prepared meals delivered quickly from nearby kitchens. No long waits, just delicious food.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon earn">
                <ChefHat size={32} />
              </div>
              <h3>Earn Money</h3>
              <p>Turn your cooking passion into profit. Share your favorite dishes and earn money doing what you love.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="how-it-works-container">
          <h2 className="section-title">How It Works</h2>
          
          <div className="process-tabs">
            <div className="tab-buttons">
              <button 
                className={`tab-button1 ${activeTab === 'buyers' ? 'active' : ''}`}
                onClick={() => handleTabClick('buyers')}
              >
                <ShoppingBag size={20} />
                For Food Lovers
              </button>
              <button 
                className={`tab-button1 ${activeTab === 'cooks' ? 'active' : ''}`}
                onClick={() => handleTabClick('cooks')}
              >
                <ChefHat size={20} />
                For Home Cooks
              </button>
            </div>
            
            {activeTab === 'buyers' && (
              <div className="tab-content active">
                <div className="process-steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Browse Local Meals</h4>
                      <p>Discover amazing homemade dishes from cooks in your neighborhood</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Place Your Order</h4>
                      <p>Simple one-tap ordering with secure payment and real-time updates</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Enjoy Fresh Food</h4>
                      <p>Get your meal delivered fresh or pick it up from the cook's location</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'cooks' && (
              <div className="tab-content active">
                <div className="process-steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Share Your Dishes</h4>
                      <p>Upload photos and details of your homemade specialties</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Receive Orders</h4>
                      <p>Get notified when neighbors want to order your delicious food</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Cook & Earn</h4>
                      <p>Prepare your dishes and earn money doing what you love</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="community-section">
        <div className="community-container">
          <div className="section-header">
            <h2 className="section-title">Join Our Growing Community</h2>
            <p className="section-subtitle">
              Be part of a movement that's bringing authentic, homemade food back to our neighborhoods
            </p>
          </div>
          
          <div className="community-grid">
            <div className="community-card">
              <div className="community-icon">
                <Users size={40} />
              </div>
              <h3>Food Lovers</h3>
              <p>Discover incredible homemade meals from passionate cooks in your area. Every dish tells a story.</p>
            </div>
            
            <div className="community-card">
              <div className="community-icon">
                <ChefHat size={40} />
              </div>
              <h3>Home Cooks</h3>
              <p>Share your culinary talents with your community and earn money doing what you love most.</p>
            </div>
            
            <div className="community-card">
              <div className="community-icon">
                <Truck size={40} />
              </div>
              <h3>Delivery Partners</h3>
              <p>Help connect cooks with customers while earning extra income on your own schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <h2>Ready to Start Your Culinary Journey?</h2>
            <p>Join thousands of food lovers and home cooks who are already part of the HomeCookt community</p>
            <div className="cta-actions">
              <Link to="/signup" className="cta-button primary large">
                <span>Join HomeCookt Today</span>
                <ArrowRight size={24} />
              </Link>
              <div className="cta-note">
                <span>✓ Quick setup process</span>
                <span>✓ Start earning immediately</span>
                <span>✓ Support local community</span>
              </div>
            </div>
          </div>
          <div className="cta-visual">
            <div className="cta-image-grid">
              <img src="https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg" alt="Delicious meal 1" />
              <img src="https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg" alt="Delicious meal 2" />
              <img src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg" alt="Delicious meal 3" />
              <img src="https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg" alt="Delicious meal 4" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;